/**
 * Danışan dosyasının ekleri.
 *
 * Bugüne kadar ıslak imzalı onam "dosyaya koydum" diye işaretleniyordu
 * ama belge sistemde durmuyordu; o belgeye ihtiyaç duyulduğu gün
 * nerede olduğunu kimse bilemezdi.
 *
 * Dosya türü sunucuda İÇERİĞE bakılarak doğrulanıyor. Buradaki
 * `accept` niteliği yalnızca dosya seçiciyi kolaylaştırıyor; bir
 * denetim değil ve öyle sunulmuyor.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ApiHatasi,
  EK_TURU_ADI,
  boyutYaz,
  ekApi,
  type Ek,
} from '../api'

const gun = (s: string) => new Date(s).toLocaleDateString('tr-TR', { dateStyle: 'medium' })

const HATA_METNI: Record<string, string> = {
  tur_kabul_edilmiyor:
    'Bu dosya türü kabul edilmiyor. PDF, JPEG, PNG ve WebP yüklenebiliyor — dosyanın adı değil içeriği belirleyici.',
  dosya_buyuk: 'Dosya 10 MB sınırını aşıyor.',
  dosya_bos: 'Dosya boş.',
  ayni_dosya_zaten_var: 'Aynı dosya bu dosyada zaten var.',
  onam_kaniti_arsivlenemez:
    'Bu ek bir onamın kanıtı. Önce onamdaki bağı kaldırın, sonra arşivleyin.',
}

export function DosyaEkler({
  danisanId,
  yazabilir,
  onDegisti,
}: {
  danisanId: string
  yazabilir: boolean
  /** Onam kanıtı bağlanabilsin diye dosyayı yeniden yükleyen üst bileşen */
  onDegisti?: () => void
}) {
  const [ekler, setEkler] = useState<Ek[] | null>(null)
  const [tur, setTur] = useState('other')
  const [hata, setHata] = useState<string | null>(null)
  const [yukleniyor, setYukleniyor] = useState(false)
  const [onizleme, setOnizleme] = useState<Ek | null>(null)
  const girdi = useRef<HTMLInputElement>(null)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      const r = await ekApi.liste(danisanId)
      setEkler(r.ekler)
    } catch {
      setHata('Ek listesi alınamadı.')
      setEkler([])
    }
  }, [danisanId])

  useEffect(() => {
    void yukle()
  }, [yukle])

  const sec = async (dosya: File | undefined) => {
    if (!dosya) return
    setHata(null)
    setYukleniyor(true)
    try {
      await ekApi.yukle(danisanId, dosya, tur)
      if (girdi.current) girdi.current.value = ''
      await yukle()
      onDegisti?.()
    } catch (e) {
      setHata(
        e instanceof ApiHatasi
          ? (HATA_METNI[e.kod] ?? 'Yüklenemedi.')
          : 'Yüklenemedi. Bağlantınızı kontrol edin.',
      )
    } finally {
      setYukleniyor(false)
    }
  }

  return (
    <div className="kart">
      <h2 style={{ margin: '0 0 4px' }}>Ekler</h2>
      <p className="alt" style={{ marginBottom: 12 }}>
        Islak imzalı onam, hekim raporu, okul yazısı, taranmış egzersiz sayfası. Dosyalar web
        kökünün dışında duruyor; yalnızca giriş yapmış kullanıcıya açılıyor.
      </p>

      {hata && <div className="uyari">{hata}</div>}

      {ekler === null ? (
        <p className="alt">Yükleniyor…</p>
      ) : ekler.length === 0 ? (
        <p className="alt">Bu dosyada ek yok.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Belge</th>
              <th>Tür</th>
              <th>Boyut</th>
              <th>Eklendi</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {ekler.map((e) => (
              <tr key={e.id}>
                <td>{e.ad}</td>
                <td>{EK_TURU_ADI[e.tur] ?? e.tur}</td>
                <td style={{ color: 'var(--muted)' }}>{boyutYaz(e.boyut)}</td>
                <td style={{ color: 'var(--muted)' }}>{gun(e.eklendi)}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {e.mime.startsWith('image/') && (
                    <button onClick={() => setOnizleme(e)}>Göster</button>
                  )}{' '}
                  <a href={ekApi.indirmeYolu(e.id)} download={e.ad}>
                    <button>İndir</button>
                  </a>{' '}
                  {yazabilir && (
                    <button
                      onClick={async () => {
                        setHata(null)
                        try {
                          await ekApi.arsivle(e.id)
                          await yukle()
                          onDegisti?.()
                        } catch (x) {
                          setHata(
                            x instanceof ApiHatasi
                              ? (HATA_METNI[x.kod] ?? 'Arşivlenemedi.')
                              : 'Arşivlenemedi.',
                          )
                        }
                      }}
                    >
                      Arşivle
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {onizleme && (
        <div style={{ marginTop: 10 }}>
          <div className="dugmeler">
            <button onClick={() => setOnizleme(null)}>Önizlemeyi kapat</button>
          </div>
          {/*
            Resim aynı kaynaktan çerezle geliyor; sunucu `nosniff` ve
            `Content-Disposition: attachment` gönderiyor, bu yüzden
            tarayıcı dosyayı sayfa olarak değil resim olarak çiziyor.
          */}
          <img
            src={ekApi.indirmeYolu(onizleme.id)}
            alt={onizleme.ad}
            style={{
              maxWidth: '100%',
              maxHeight: 520,
              border: '1px solid var(--line)',
              borderRadius: 8,
            }}
          />
        </div>
      )}

      {yazabilir && (
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 10, marginTop: 10 }}>
          <div className="satir">
            <label>
              Tür
              <select value={tur} onChange={(e) => setTur(e.target.value)}>
                {Object.entries(EK_TURU_ADI).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Dosya
              <input
                ref={girdi}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                disabled={yukleniyor}
                onChange={(e) => void sec(e.target.files?.[0])}
              />
            </label>
          </div>
          <p className="alt" style={{ marginTop: -4 }}>
            {yukleniyor
              ? 'Yükleniyor…'
              : 'PDF, JPEG, PNG veya WebP · en çok 10 MB. Dosya türü içeriğinden doğrulanıyor.'}
          </p>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */

/**
 * Onama kanıt bağlama.
 *
 * Onamlar bölümünün içinde duruyor: onamın yanında "taraması var mı"
 * sorusu, ek listesinde durduğunda görünmez oluyor.
 */
export function OnamKaniti({
  onamId,
  danisanId,
  mevcutEkId,
  yazabilir,
  onDegisti,
}: {
  onamId: string
  danisanId: string
  mevcutEkId: string | null
  yazabilir: boolean
  onDegisti: () => void
}) {
  const [ekler, setEkler] = useState<Ek[]>([])
  const [acik, setAcik] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  useEffect(() => {
    if (!acik) return
    ekApi
      .liste(danisanId)
      .then((r) => setEkler(r.ekler))
      .catch(() => setHata('Ek listesi alınamadı.'))
  }, [acik, danisanId])

  if (mevcutEkId) {
    return (
      <span className="alt" style={{ margin: 0 }}>
        <a href={ekApi.indirmeYolu(mevcutEkId)} download>
          taraması var
        </a>
        {yazabilir && (
          <>
            {' · '}
            <button
              style={{ padding: '1px 6px', fontSize: 12 }}
              onClick={async () => {
                await ekApi.kanitBagla(onamId, null)
                onDegisti()
              }}
            >
              bağı kaldır
            </button>
          </>
        )}
      </span>
    )
  }

  if (!yazabilir) {
    return (
      <span className="alt" style={{ margin: 0 }}>
        tarama yok
      </span>
    )
  }

  return (
    <span className="alt" style={{ margin: 0 }}>
      {hata ? (
        hata
      ) : !acik ? (
        <button style={{ padding: '1px 6px', fontSize: 12 }} onClick={() => setAcik(true)}>
          tarama bağla
        </button>
      ) : ekler.length === 0 ? (
        <>önce Ekler bölümünden taramayı yükleyin</>
      ) : (
        <select
          defaultValue=""
          onChange={async (e) => {
            if (!e.target.value) return
            try {
              await ekApi.kanitBagla(onamId, e.target.value)
              setAcik(false)
              onDegisti()
            } catch {
              setHata('bağlanamadı')
            }
          }}
        >
          <option value="">Ek seç…</option>
          {ekler.map((x) => (
            <option key={x.id} value={x.id}>
              {x.ad}
            </option>
          ))}
        </select>
      )}
    </span>
  )
}
